import { ReactNode, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import Alert from '@mui/material/Alert';
import { alpha, Theme } from '@mui/material/styles';
import { observer } from 'mobx-react-lite';
import { Icon } from '@iconify/react';
import * as Yup from 'yup';
import axios from 'axios';
import { userApi } from 'api';
import type { User } from 'types/api';
import { personalDataStore } from 'stores/personalDataStore';
import HelpTooltip from './HelpTooltip';

// ----------------------------------------------------------------------
// Standalone, reusable modal for the three "career" fields:
// Сфера деятельности / Должность / Уровень. Mirrors the inline block in
// UserForm (same async autocomplete + validation) but is fully
// self-contained and is NOT wired into UserForm.
//
// Usage:
//   <SpecialityModal
//     open={open}
//     onClose={() => setOpen(false)}
//     initialValues={{ speciality, position, specialityLevel }}
//     onSaved={(values) => ...}      // optional, fires after success
//     onSubmit={(values) => ...}     // optional: take over persistence
//   />
// When `onSubmit` is omitted the modal saves to the current user's
// profile itself via userApi.updateUser.
// ----------------------------------------------------------------------

export type SpecialityValues = {
  speciality: string;
  position: string;
  specialityLevel: User['specialityLevel'] | undefined;
};

type SpecialityModalProps = {
  open: boolean;
  onClose: () => void;
  /** Pre-fill the fields. Falls back to the current user when omitted. */
  initialValues?: Partial<SpecialityValues>;
  /** Fired after a successful save with the saved values. */
  onSaved?: (values: SpecialityValues) => void;
  /**
   * If provided, the modal calls this instead of saving to the profile
   * itself — the parent owns persistence. May be async; throwing keeps
   * the dialog open and shows the error.
   */
  onSubmit?: (values: SpecialityValues) => void | Promise<void>;
  title?: ReactNode;
  description?: ReactNode;
};

// Design-system field styling (YYNY.AI / Veilstaff): 8px radius, hairline
// border that turns into a 2px indigo ring on focus and a magenta-pink ring
// on error. Kept local so the modal has no hidden dependencies.
const fieldSx = (theme: Theme) => ({
  '& .MuiInputBase-root': {
    borderRadius: `${theme.shape.borderRadius}px`,
    backgroundColor: theme.palette.common.white
  },
  '& .MuiInputBase-input': {
    fontSize: 15
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: alpha(theme.palette.grey[500], 0.32),
    transition: theme.transitions.create('border-color', { duration: 120 })
  },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: alpha(theme.palette.grey[500], 0.6)
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
    borderWidth: 2
  },
  '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.error.main
  },
  '& .MuiOutlinedInput-root.Mui-disabled': {
    backgroundColor: theme.palette.grey[200],
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: alpha(theme.palette.grey[500], 0.24)
    }
  },
  '& .MuiFormHelperText-root': {
    marginLeft: '4px',
    marginTop: '6px',
    fontSize: 12,
    lineHeight: '18px',
    whiteSpace: 'pre-line'
  }
});

// Label sits tight above the field (design system: 6px gap, h6 weight).
const FieldLabel = ({
  htmlFor,
  required,
  children
}: {
  htmlFor?: string;
  required?: boolean;
  children: ReactNode;
}) => (
  <Typography
    component="label"
    htmlFor={htmlFor}
    sx={{
      display: 'block',
      mb: 0.75,
      fontSize: 14,
      lineHeight: '22px',
      fontWeight: 600,
      color: 'text.primary'
    }}
  >
    {children}
    {required ? (
      <Box component="span" aria-hidden sx={{ color: 'text.disabled', ml: 0.5 }}>
        *
      </Box>
    ) : null}
  </Typography>
);

const validationSchema = Yup.object({
  speciality: Yup.string()
    .trim()
    .required('Укажите сферу деятельности')
    .max(100, 'Слишком длинное значение'),
  position: Yup.string().trim().required('Укажите должность').max(100, 'Слишком длинное значение'),
  specialityLevel: Yup.mixed<User['specialityLevel']>()
    .oneOf(['JUNIOR', 'MIDDLE', 'SENIOR'], 'Выберите уровень')
    .required('Выберите уровень')
});

const LEVEL_LABELS: Record<string, string> = {
  JUNIOR: 'Начинающий',
  MIDDLE: 'С опытом',
  SENIOR: 'Эксперт'
};

const SpecialityModal = observer(
  ({
    open,
    onClose,
    initialValues,
    onSaved,
    onSubmit,
    title = 'Сфера деятельности и уровень',
    description = 'Укажите специализацию, должность и уровень. На их основе ИИ сформирует подходящие вопросы для интервью.'
  }: SpecialityModalProps) => {
    const user = personalDataStore.user;
    const [needCheckPosition, setNeedCheckPosition] = useState(false);

    const resolvedInitial = useMemo<SpecialityValues>(
      () => ({
        speciality: initialValues?.speciality ?? user?.speciality ?? '',
        position: initialValues?.position ?? user?.position ?? '',
        specialityLevel: initialValues?.specialityLevel ?? user?.specialityLevel
      }),
      [initialValues, user]
    );

    const [values, setValues] = useState<SpecialityValues>(resolvedInitial);
    const [specializationInput, setSpecializationInput] = useState(resolvedInitial.speciality);
    const [positionInput, setPositionInput] = useState(resolvedInitial.position);
    const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof SpecialityValues, string>>>(
      {}
    );
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    // Re-sync the form every time the dialog is (re)opened.
    useEffect(() => {
      if (open) {
        setValues(resolvedInitial);
        setSpecializationInput(resolvedInitial.speciality);
        setPositionInput(resolvedInitial.position);
        setFieldErrors({});
        setError(null);
      }
    }, [open, resolvedInitial]);

    const selectedSpecializationId = useMemo(() => {
      const found = personalDataStore.specializations.find((s) => s.name === values.speciality);
      return found?.id ? Number(found.id) : null;
    }, [values.speciality, personalDataStore.specializations]);

    useEffect(() => {
      if (!open) return undefined;
      const handler = setTimeout(() => {
        if (values.position && !specializationInput) {
          personalDataStore.fetchSpecializationsByPositionName(values.position);
        } else {
          personalDataStore.fetchSpecializations(specializationInput);
        }
      }, 300);
      return () => clearTimeout(handler);
    }, [open, specializationInput, values.position]);

    useEffect(() => {
      if (!open) return undefined;
      const handler = setTimeout(() => {
        if (selectedSpecializationId) {
          personalDataStore.fetchPositionsBySpecializationId(
            positionInput,
            selectedSpecializationId
          ).then(() => {
            if(!needCheckPosition) return
            if(!personalDataStore.positions.find(i => i.name === values.position)) {
              setValues((prev) => ({ ...prev, position: '' }));
            }
          })
        } else {
          personalDataStore.fetchAllPositions(positionInput);
        }
      }, 300);
      return () => clearTimeout(handler);
    }, [open, positionInput, selectedSpecializationId, needCheckPosition]);

    const showPositionAutocomplete = useMemo(
      () => values.speciality !== 'Другое',
      [values.speciality]
    );

    const handleSpecialityChange = (_event: unknown, newValue: string | null) => {
      if(values.position.length) {
        setNeedCheckPosition(true)
      }
      setValues((prev) => ({ ...prev, speciality: newValue || '' }));
      if (fieldErrors.speciality) {
        setFieldErrors((prev) => ({ ...prev, speciality: undefined }));
      }
    };

    const handlePositionChange = (_event: unknown, newValue: string | null) => {
      setValues((prev) => ({ ...prev, position: newValue || '' }));
      if (fieldErrors.position) {
        setFieldErrors((prev) => ({ ...prev, position: undefined }));
      }
    };

    const handleLevelChange = (e: { target: { value: string } }) => {
      setValues((prev) => ({
        ...prev,
        specialityLevel: e.target.value as User['specialityLevel']
      }));
      if (fieldErrors.specialityLevel) {
        setFieldErrors((prev) => ({ ...prev, specialityLevel: undefined }));
      }
    };

    const handleSave = async () => {
      setSaving(true);
      setError(null);
      setFieldErrors({});
      try {
        const validated = (await validationSchema.validate(values, {
          abortEarly: false
        })) as SpecialityValues;

        if (onSubmit) {
          await onSubmit(validated);
        } else {
          if (!personalDataStore.userId) {
            setError('Не удалось определить пользователя');
            return;
          }
          await userApi.updateUser(personalDataStore.userId, {
            speciality: validated.speciality,
            position: validated.position,
            specialityLevel: validated.specialityLevel
          });
          await personalDataStore.fetchAndApplyUser(personalDataStore.userId);
        }

        onSaved?.(validated);
        onClose();
      } catch (e) {
        if (e instanceof Yup.ValidationError) {
          const nextFieldErrors: Partial<Record<keyof SpecialityValues, string>> = {};
          e.inner.forEach((issue) => {
            if (issue.path && !nextFieldErrors[issue.path as keyof SpecialityValues]) {
              nextFieldErrors[issue.path as keyof SpecialityValues] = issue.message;
            }
          });
          setFieldErrors(nextFieldErrors);
        } else if (axios.isAxiosError(e)) {
          const message = Array.isArray(e.response?.data?.message)
            ? e.response?.data?.message
            : [e.response?.data?.message];
          setError(message.filter(Boolean).join('\n') || 'Не удалось сохранить данные');
        } else {
          setError('Не удалось сохранить данные');
        }
      } finally {
        setSaving(false);
      }
    };

    return (
      <Dialog
        open={open}
        onClose={saving ? undefined : onClose}
        aria-labelledby="speciality-dialog-title"
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { p: 2, pt: 0 } }}
      >
        <DialogTitle id="speciality-dialog-title">{title}</DialogTitle>
        <DialogContent>
          {description ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {description}
            </Typography>
          ) : null}

          <Stack spacing={2.5}>
            <Box>
              <Stack direction="row" spacing={1.5} alignItems="flex-end">
                <Box sx={{ flex: 1 }}>
                  <FieldLabel htmlFor="modal-speciality" required>
                    Сфера деятельности
                  </FieldLabel>
                  <Autocomplete
                    id="modal-speciality"
                    fullWidth
                    size="small"
                    options={personalDataStore.specializations.map((s) => s.name)}
                    value={values.speciality || null}
                    onChange={handleSpecialityChange}
                    inputValue={specializationInput}
                    onInputChange={(_event, newInputValue) => setSpecializationInput(newInputValue)}
                    filterOptions={() => personalDataStore.specializations.map((s) => s.name)}
                    sx={fieldSx}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Выберите специализацию"
                        required
                        error={Boolean(fieldErrors.speciality)}
                        helperText={fieldErrors.speciality}
                        inputProps={{
                          ...params.inputProps,
                          'aria-label': 'Сфера деятельности'
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        {option}
                      </Box>
                    )}
                    noOptionsText="Сфера деятельности не найдена"
                    clearOnEscape
                  />
                </Box>
                <HelpTooltip />
              </Stack>
            </Box>

            <Box>
              <FieldLabel htmlFor="modal-position" required>
                Должность
              </FieldLabel>
              {showPositionAutocomplete ? (
                <Autocomplete
                  id="modal-position"
                  fullWidth
                  size="small"
                  options={personalDataStore.positions.map((p) => p.name)}
                  value={values.position || null}
                  onChange={handlePositionChange}
                  inputValue={positionInput}
                  onInputChange={(_event, newInputValue) => setPositionInput(newInputValue)}
                  filterOptions={(options) => options}
                  sx={fieldSx}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Выберите должность"
                      required
                      error={Boolean(fieldErrors.position)}
                      helperText={fieldErrors.position}
                      inputProps={{
                        ...params.inputProps,
                        'aria-label': 'Должность'
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      {option}
                    </Box>
                  )}
                  noOptionsText="Должность не найдена"
                  clearOnEscape
                />
              ) : (
                <TextField
                  id="modal-position"
                  size="small"
                  placeholder="Укажите должность"
                  disabled={!values.speciality}
                  required
                  fullWidth
                  onChange={(e) => {
                    setPositionInput(e.currentTarget.value);
                    handlePositionChange(e, e.currentTarget.value);
                  }}
                  value={positionInput}
                  error={Boolean(fieldErrors.position)}
                  helperText={fieldErrors.position}
                  inputProps={{
                    'aria-label': 'Должность'
                  }}
                  sx={fieldSx}
                />
              )}
            </Box>

            <Box>
              <FieldLabel htmlFor="modal-specialityLevel" required>
                Уровень
              </FieldLabel>
              <TextField
                id="modal-specialityLevel"
                select
                fullWidth
                size="small"
                value={values.specialityLevel ?? ''}
                onChange={handleLevelChange}
                required
                error={Boolean(fieldErrors.specialityLevel)}
                helperText={fieldErrors.specialityLevel}
                inputProps={{ 'aria-label': 'Уровень' }}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (value) =>
                    value ? (
                      LEVEL_LABELS[value as string]
                    ) : (
                      <Box component="span" sx={{ color: 'text.disabled' }}>
                        Выберите уровень
                      </Box>
                    )
                }}
                sx={fieldSx}
              >
                <MenuItem value="JUNIOR">Начинающий</MenuItem>
                <MenuItem value="MIDDLE">С опытом</MenuItem>
                <MenuItem value="SENIOR">Эксперт</MenuItem>
              </TextField>
            </Box>

            {error ? (
              <Alert
                severity="error"
                variant="outlined"
                role="alert"
                sx={{ whiteSpace: 'pre-line' }}
              >
                {error}
              </Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="inherit" disabled={saving}>
            Отмена
          </Button>
          <Button onClick={handleSave} variant="contained" color="primary" disabled={saving}>
            {saving ? 'Сохранение...' : 'Сохранить'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
);

export default SpecialityModal;
